/*
	This is a reducer that ONLY HOUSES ANALYTICS calls.
	It contains no internal state.
	It only subscribes to the actions in the signup ducks file,
	and composes the state to
*/

import Analytics from "./util/Analytics";
import { prettifyData } from "./util/helpers";
import { STEPS } from "apps/onboarding/util/constants";
import { isReboarding } from "apps/onboarding/util/helper";
import ClientStats from "shared/util/ClientStats";

import {
	NEXT_STEP,
	RECORD_QUESTION_RESPONSE,
	NEXT_ON_QUESTION_INTRO,
	SUBMIT_QUESTION_RESPONSE,
	NEXT_ON_STEP_INTRO,
	COMPLETE_ONBOARDING_SUCCESS,
	SUBMIT_QUESTION_CARD_RESPONSE,
	COMPLETE_QUESTION_CARDS,
	CONFIRM_RECAPTCHA_SUCCESS,
} from "ducks/onboarding";

// ------------------------- helpers -------------------------
const _onboardingFieldValueMap = {
	[STEPS.LOOKINGFOR]: {
		ageMin: "desired min age",
		ageMax: "desired max age",
		hereFor: "desired relationship",
	},
	[STEPS.PHOTO]: {
		photo_count: "Photo count",
	},
	[STEPS.PHOTOS]: {
		facebookCount: "Facebook",
		photoCount: "Photo count",
	},
	[STEPS.ESSAY]: {
		spaceless_char_count: "character count (no spaces)",
		essay_text: "Essay Text",
	},
	[STEPS.PROFILE]: {
		spaceless_char_count: "character count (no spaces)",
	},
	[STEPS.QUESTIONS_CONVO]: {
		numQuestionsAnswered: "Questions answered",
	},
};

const _gentationMap = {
	"1": "Straight men",
	"2": "Straight women",
	"4": "Gay men",
	"8": "Gay women",
	"16": "Bi men",
	"32": "Bi women",
	"48": "Bi men and women",
	"17": "Men who like women",
	"34": "Women who like men",
	"40": "Women who like women",
	"20": "Men who like men",
	"54": "Everybody into bi men",
	"57": "Everybody into bi women",
	"21": "Men",
	"42": "Women",
	"63": "Everybody",
};

function _parseOnboardingQuestionData(question) {
	const parsedData = {};
	parsedData.eventName = Analytics.eventNames.questionsSubmitted;
	parsedData.data = {
		"question id": question.id,
		"question text": question.text,
		"skipped": !!question.skip,
		"source": "onboarding",
	};

	if (!question.skip) {
		parsedData.data["user responses"] = question.answers[question.answer - 1];
		parsedData.data["answered publicly"] = true;
		parsedData.data.importance = question.important ? "high" : "low";
	}

	return parsedData;
}

function _parseOnboardingQuestionResponse(response) {
	const { question, responsePosition, isTheirs } = response;
	const parsedResponse = {};
	parsedResponse.data = {
		"Question id": question.id,
		"Question text": question.text,
	};

	if (isTheirs) {
		parsedResponse.eventName = Analytics.eventNames.onboardingSelectedAcceptedResponseOnQuestion;
		parsedResponse.data["Accepted Response"] = question.answers[responsePosition - 1];
	} else {
		parsedResponse.eventName = Analytics.eventNames.onboardingSelectedMyResponseOnQuestion;
		parsedResponse.data["User Response"] = question.answers[responsePosition - 1];
	}

	return parsedResponse;
}

function _getOnboardingStepData(stepName, actionParameters) {
	const dataFields = _onboardingFieldValueMap[stepName];
	const metaData = {};

	if (!dataFields || !actionParameters) {
		return null;
	}

	Object.keys(actionParameters).forEach((parameter) => {
		const dataField = dataFields[parameter];

		if (!dataField) {
			return;
		}

		// if it's looking for here for field, prettify the data
		if (dataField === _onboardingFieldValueMap[STEPS.LOOKINGFOR].hereFor) {
			metaData[dataField] = prettifyData(actionParameters[parameter]);
		}	else if (dataField === _onboardingFieldValueMap[STEPS.LOOKINGFOR].gentation) {
			metaData[dataField] = _gentationMap[actionParameters[parameter]];
		} else if (dataField === 	_onboardingFieldValueMap[STEPS.QUESTIONS_CONVO]) {
			metaData[dataField] = _parseOnboardingQuestionData(actionParameters[parameter]);
		} else if (dataField === _onboardingFieldValueMap[STEPS.PHOTOS].facebookCount) {
			// add facebook photo source
			metaData["Photo source"] = { [dataField]: actionParameters[parameter] };
		} else {
			metaData[dataField] = actionParameters[parameter];
		}
	});

	return metaData;
}


// ////////////////////////
// REDUCER ///////////////
// //////////////////////

export default function onboarding(state, action) {
	switch (action.type) {
		case NEXT_STEP: {
			const { activeStep } = state;

			// if there is an activeStep, record it
			if (activeStep) {
				const eventName = Analytics.eventNames.getOnboardingStepEventName(activeStep.name);
				const metaData = _getOnboardingStepData(activeStep.name, action.params);

				if (!metaData || !Object.keys(metaData).length) {
					return;
				}

				// add onboarding upload source to all photos
				if (activeStep.name === STEPS.PHOTO ||
					activeStep.name === STEPS.PHOTOS ||
					activeStep.name === STEPS.PROFILE
				) {
					metaData["Upload source"] = "onboarding";
				}

				Analytics.logUIEvent(
					eventName,
					{ data: metaData },
				);

				// some more straightforward onboarding stuff
				if (activeStep.name === STEPS.ESSAY || activeStep.name === STEPS.PROFILE) {
					Analytics.logUIEvent(
						Analytics.eventNames.profileEditEssay,
						{
							data: {
								"source of edit": "onboarding",
								"last edit time": Math.floor(new Date().getTime() / 1000),
								"essay edited": "My self-summary",
								"character count (no spaces)": action.params.spaceless_char_count,
								"has photo": false,
								"category name": "about me",
							},
						},
					);
				}
			}

			return;
		}

		case RECORD_QUESTION_RESPONSE: {
			const response = _parseOnboardingQuestionResponse(action.response);

			Analytics.logUIEvent(
				response.eventName,
				{ data:	response.data },
			);
			return;
		}

		case SUBMIT_QUESTION_RESPONSE: {
			const response = _parseOnboardingQuestionData(action.questionResponse);

			Analytics.logUIEvent(
				response.eventName,
				{ data:	response.data },
			);
			return;
		}

		case NEXT_ON_QUESTION_INTRO: {
			Analytics.logUIEvent(Analytics.eventNames.onboardingSelectedNextOnQuestionIntro);
			return;
		}

		case NEXT_ON_STEP_INTRO: {
			if (state.activeStep.name === STEPS.WHAT_I_WANT) {
				Analytics.logUIEvent(Analytics.eventNames.onboardingSelectedMyMatchInterstitial);
			} else if (state.activeStep.name === STEPS.BASICS) {
				Analytics.logUIEvent(Analytics.eventNames.onboardingSelectedAboutMeInterstitial);
			} else if (state.activeStep.name === STEPS.PASSIONS_PICKER) {
				Analytics.logUIEvent(
					Analytics.eventNames.passionsSelectedAddTopics,
					{
						data: {
							"flow": isReboarding() ? "reboarding" : "onboarding",
						},
					},
				);
			}

			return;
		}

		case COMPLETE_QUESTION_CARDS: {
			Analytics.logUIEvent(Analytics.eventNames.onboardingQuestionsCardCompleted);
			return;
		}

		case COMPLETE_ONBOARDING_SUCCESS: {
			Analytics.logUIEvent(
				Analytics.eventNames.onboardingFinishedFunnel,
				{
					data: {
						"profile_picture": state.userThumb,
					},
				},
			);

			ClientStats.update({
				statname: "onboarding funnel - completed onboarding",
				unique: true,
			});

			return;
		}

		case SUBMIT_QUESTION_CARD_RESPONSE: {
			const response = action.data;

			Analytics.logUIEvent(
				Analytics.eventNames.onboardingSelectedResponseOnQuestionCard,
				{
					data:	{
						"Question id": response.qid,
						"User Response": response.answer,
					},
				},
			);
			return;
		}

		case CONFIRM_RECAPTCHA_SUCCESS: {
			const { payload: { score, isCaptchaShowing, page, href } } = action;
			Analytics.logUIEvent(
				Analytics.eventNames.recaptchaScore,
				{
					data: {
						score,
						isCaptchaShowing,
						page,
						href,
					},
				},
			);
			return;
		}

		default: {
			return;
		}
	}
}



// WEBPACK FOOTER //
// ./src/analytics/onboarding.js